import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuth } from '../store';
import { loadEvent, formatCurrency } from '../utils';
import { getUserEvents, getPublicEvents, addEventToUser } from '../services/firestore';
import type { Event } from '../types';

/**
 * My Games Page
 * Shows:
 * - Events I'm Running (admin)
 * - Events I'm In (participant)
 * - Available Events (public)
 */
export function MyGamesPage() {
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [runningEvents, setRunningEvents] = useState<Event[]>([]);
  const [participatingEvents, setParticipatingEvents] = useState<Event[]>([]);
  const [finishedEvents, setFinishedEvents] = useState<Event[]>([]);
  const [publicEvents, setPublicEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'running' | 'participating' | 'finished' | 'available'>('running');

  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (!authLoading && (!isAuthenticated || !currentUser)) {
      navigate('/');
      return;
    }
    
    // Don't load events until auth is ready
    if (authLoading || !isAuthenticated || !currentUser) {
      return;
    }

    // Load user's events
    const loadEvents = async () => {
      try {
        const eventIds = await getUserEvents(currentUser.id);
        
        // Load all events in PARALLEL for much faster loading
        const eventPromises = eventIds.map(async (eventId) => {
          try {
            let event = await loadEvent(eventId);
            if (event) {
              // ALWAYS merge template fights if this is an event instance
              // (This ensures we get the latest template updates, even for old events)
              if (event.templateId) {
                const { mergeTemplateIntoEvent } = await import('../utils/templateMerge');
                event = await mergeTemplateIntoEvent(event);
              }
              return event;
            }
            return null;
          } catch (err) {
            console.error(`Failed to load event ${eventId}:`, err);
            return null;
          }
        });
        
        // Wait for all events to load
        const loadedEventsWithNulls = await Promise.all(eventPromises);
        const loadedEvents = loadedEventsWithNulls.filter((e): e is Event => e !== null);

        // Separate into admin events, participant events, and finished events
        const running: Event[] = [];
        const participating: Event[] = [];
        const finished: Event[] = [];
        
        for (const event of loadedEvents) {
          // Check if user is the admin
          const adminParticipant = event.participants.find(p => p.id === event.adminId);
          const isAdmin = adminParticipant && 
            (adminParticipant.name.toLowerCase() === currentUser.username.toLowerCase() ||
             (currentUser.email && adminParticipant.name.toLowerCase().includes(currentUser.email.split('@')[0].toLowerCase())));
          
          // Check if user is a participant (not just admin, but actually joined/betting)
          const userParticipant = event.participants.find(p => 
            p.name.toLowerCase() === currentUser.username.toLowerCase() ||
            (currentUser.email && p.name.toLowerCase().includes(currentUser.email.split('@')[0].toLowerCase()))
          );
          
          // Check if event is finished
          const isFinished = event.finished || event.status === 'completed';
          
          if (isFinished) {
            // Add finished events to separate tab
            finished.push(event);
          } else {
            // Add to running if user is admin
            if (isAdmin) {
              running.push(event);
            }
            
            // Add to participating if user is a participant (can be in both!)
            if (userParticipant) {
              participating.push(event);
            }
          }
        }

        setRunningEvents(running);
        setParticipatingEvents(participating);
        setFinishedEvents(finished);
      } catch (err) {
        console.error('Failed to load events:', err);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [currentUser, isAuthenticated, authLoading, navigate]);

  // Load public events when Available tab is active
  useEffect(() => {
    if (activeTab === 'available' && currentUser) {
      const loadPublicEvents = async () => {
        try {
          const pubEvents = await getPublicEvents();
          setPublicEvents(pubEvents);
        } catch (err) {
          console.error('Failed to load public events:', err);
        }
      };
      loadPublicEvents();
    }
  }, [activeTab, currentUser]);

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !currentUser) {
    return null; // Will redirect
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading your games...</p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (event: Event) => {
    if (event.finished) {
      return <Badge variant="default" size="sm">Finished</Badge>;
    }
    if (event.status === 'live') {
      return <Badge variant="error" size="sm">Live</Badge>;
    }
    return <Badge variant="info" size="sm">Upcoming</Badge>;
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">My Games</h1>
            <p className="text-sm text-secondary-text">{currentUser.username}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/leaderboard')}
            >
              Leaderboard
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/account')}
            >
              Account
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/create')}
            >
              Create Event
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('running')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'running'
                ? 'text-primary-text border-b-2 border-primary-blue'
                : 'text-secondary-text hover:text-primary-text'
            }`}
          >
            Events I'm Running ({runningEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('participating')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'participating'
                ? 'text-primary-text border-b-2 border-primary-blue'
                : 'text-secondary-text hover:text-primary-text'
            }`}
          >
            Events I'm In ({participatingEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('finished')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'finished'
                ? 'text-primary-text border-b-2 border-primary-blue'
                : 'text-secondary-text hover:text-primary-text'
            }`}
          >
            Finished Events ({finishedEvents.length})
          </button>
          <button
            onClick={() => setActiveTab('available')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'available'
                ? 'text-primary-text border-b-2 border-primary-blue'
                : 'text-secondary-text hover:text-primary-text'
            }`}
          >
            Available Events
          </button>
        </div>

        {/* Events List */}
        {activeTab === 'available' ? (
          // Show public events
          publicEvents.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-8">
                <p className="text-secondary-text mb-4">No public events available</p>
                <p className="text-sm text-muted-text">
                  Public events will appear here when created
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-4">
              {publicEvents.map((event) => {
                const totalBets = event.participants.reduce((sum, p) => sum + p.bets.length, 0);
                
                return (
                  <Card key={event.id} padding="md">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h2 className="text-lg font-semibold text-primary-text">{event.name}</h2>
                          {getStatusBadge(event)}
                        </div>
                        {event.adminUsername && (
                          <p className="text-sm text-secondary-text">
                            Created by: {event.adminUsername}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-tertiary-bg rounded-lg">
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Participants</div>
                        <div className="font-semibold text-primary-text">
                          {event.participants.length}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Fights</div>
                        <div className="font-semibold text-primary-text">
                          {event.fights.length}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Total Bets</div>
                        <div className="font-semibold text-primary-text">
                          {totalBets}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => navigate(`/join/${event.id}`)}
                      >
                        Join Event
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )
        ) : (
          (() => {
            const eventsToDisplay = activeTab === 'running' ? runningEvents : 
                                   activeTab === 'participating' ? participatingEvents :
                                   finishedEvents;
            
            if (eventsToDisplay.length === 0) {
              return (
                <Card padding="lg">
                  <div className="text-center py-8">
                    <p className="text-secondary-text mb-4">
                      {activeTab === 'running' 
                        ? "You're not running any events yet" 
                        : activeTab === 'participating'
                        ? "You're not participating in any events yet"
                        : "No finished events"}
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => navigate('/create')}
                    >
                      Create Your First Event
                    </Button>
                  </div>
                </Card>
              );
            }
            
            return (
              <div className="space-y-4">
                {eventsToDisplay.map((event) => {
                  const participant = event.participants.find(p => 
                    p.name.toLowerCase() === currentUser!.username.toLowerCase() ||
                    (currentUser!.email && p.name.toLowerCase().includes(currentUser!.email.split('@')[0].toLowerCase()))
                  );
              
              return (
                <Card key={event.id} padding="md">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h2 className="text-lg font-semibold text-primary-text">{event.name}</h2>
                        {getStatusBadge(event)}
                      </div>
                      <p className="text-sm text-secondary-text">
                        Created: {new Date(event.createdAt).toLocaleDateString()}
                      </p>
                      {event.finishedAt && (
                        <p className="text-sm text-secondary-text">
                          Finished: {new Date(event.finishedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {participant && (
                    <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-tertiary-bg rounded-lg">
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Balance</div>
                        <div className="font-semibold text-primary-text">
                          {formatCurrency(participant.totalAvailable)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Winnings</div>
                        <div className="font-semibold text-success-green">
                          {formatCurrency(participant.winningsBalance)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Bets</div>
                        <div className="font-semibold text-primary-text">
                          {participant.bets.length}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {participant ? (
                      // User is a participant - show Enter Event button
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={async () => {
                          // Add event to user's events list if not already there
                          if (currentUser && !currentUser.events.includes(event.id)) {
                            await addEventToUser(currentUser.id, event.id);
                          }
                          navigate(`/event/${event.id}`);
                        }}
                      >
                        Enter Event
                      </Button>
                    ) : (
                      // User is not a participant yet - show Enter Event button
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={async () => {
                          // Add event to user's events list if not already there
                          if (currentUser && !currentUser.events.includes(event.id)) {
                            await addEventToUser(currentUser.id, event.id);
                          }
                          navigate(`/join/${event.id}`);
                        }}
                      >
                        Enter Event
                      </Button>
                    )}
                    {/* Show Admin button if in "Events I'm Running" tab OR if user is admin */}
                    {(activeTab === 'running' || (participant && participant.id === event.adminId)) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/admin/${event.id}`)}
                      >
                        Admin
                      </Button>
                    )}
                  </div>
                </Card>
              );})}
            </div>
          );
        })()
        )}
      </div>
    </div>
  );
}

